import React, { ReactNode } from 'react';
import ContactPageComponent, {
  ContactPageCompanyCard,
  ContactPageDepartment,
} from '@fuww/library/src/ContactPage';
import SubscriptionFaq from '@fuww/library/src/SubscriptionFlowForm/Faq';
import EditIcon from '@fuww/library/src/Icons/Edit';
import CampaignIcon from '@fuww/library/src/Icons/Campaign';
import StorefrontIcon from '@fuww/library/src/Icons/Storefront';
import WorkIcon from '@fuww/library/src/Icons/Work';
import NewspaperIcon from '@fuww/library/src/Icons/Newspaper';
import BusinessIcon from '@fuww/library/src/Icons/Business';
import { IntlShape, useIntl } from 'react-intl';
import LinkAndAnchor from './LinkAndAnchor';
import messages from '../lib/messages.json';
import HomeLink from './HomeLink';
import { useSiteContext } from './SiteContext';
import contactFaq from '../lib/contactFaq';

interface DepartmentData {
  id: string;
  labelMessage: keyof typeof messages;
  subtitleMessage: keyof typeof messages;
  icon: ReactNode;
  emailLocal: string;
  phone?: string;
}

const departmentData: DepartmentData[] = [
  {
    id: 'editorial',
    labelMessage: 'messages.contact.department.editorial',
    subtitleMessage: 'messages.contact.department.editorial.subtitle',
    icon: <EditIcon />,
    emailLocal: 'news',
    phone: '+31 20 408 4598',
  },
  {
    id: 'media',
    labelMessage: 'messages.contact.department.media',
    subtitleMessage: 'messages.contact.department.media.subtitle',
    icon: <CampaignIcon />,
    emailLocal: 'media',
  },
  {
    id: 'marketplace',
    labelMessage: 'messages.contact.department.marketplace',
    subtitleMessage: 'messages.contact.department.marketplace.subtitle',
    icon: <StorefrontIcon />,
    emailLocal: 'marketplace',
  },
  {
    id: 'jobs',
    labelMessage: 'messages.contact.department.jobs',
    subtitleMessage: 'messages.contact.department.jobs.subtitle',
    icon: <WorkIcon />,
    emailLocal: 'jobs',
    phone: '+31 20 237 4310',
  },
  {
    id: 'newsSubscription',
    labelMessage: 'messages.contact.department.newsSubscription',
    subtitleMessage: 'messages.contact.department.newsSubscription.subtitle',
    icon: <NewspaperIcon />,
    emailLocal: 'subscriptions',
    phone: '+31 20 237 4310',
  },
  {
    id: 'office',
    labelMessage: 'messages.contact.department.office',
    subtitleMessage: 'messages.contact.department.office.subtitle',
    icon: <BusinessIcon />,
    emailLocal: 'officemanager',
  },
];

const buildDepartments = (
  intl: IntlShape,
): ContactPageDepartment[] => (
  departmentData.map((department) => ({
    id: department.id,
    label: intl.formatMessage(messages[department.labelMessage]),
    subtitle: intl.formatMessage(messages[department.subtitleMessage]),
    icon: department.icon,
    email: `${department.emailLocal}@fashionunited.com`,
    phone: department.phone,
  }))
);

interface CompanyCardData {
  id: string;
  titleMessage: keyof typeof messages;
  entity?: string;
  addressLines?: string[];
  email?: string;
  phone?: string;
  phoneNoteMessage?: keyof typeof messages;
  people?: {
    labelMessage: keyof typeof messages;
    nameMessage: keyof typeof messages;
  }[];
  codes: {
    labelMessage: keyof typeof messages;
    value: string;
  }[];
}

const mediaAddress = ['Hoogoorddreef 56S', '1101 BE Amsterdam'];
const mediaEmail = 'media@fashionunited.com';
const mediaKvk = '32078229';
const mediaVatBtw = 'NL808503558B01';

const companyCardData: CompanyCardData[] = [
  {
    id: 'media',
    titleMessage: 'messages.contact.company.media.title',
    entity: 'Infonomy BV',
    addressLines: mediaAddress,
    email: mediaEmail,
    people: [
      {
        labelMessage: 'messages.contact.companyInfo.ceo',
        nameMessage: 'messages.contact.company.media.ceo',
      },
      {
        labelMessage: 'messages.contact.companyInfo.editorInChief',
        nameMessage: 'messages.contact.company.media.editor',
      },
    ],
    codes: [
      { labelMessage: 'messages.contact.companyInfo.kvk', value: mediaKvk },
      {
        labelMessage: 'messages.contact.companyInfo.vatBtw',
        value: mediaVatBtw,
      },
      {
        labelMessage: 'messages.contact.companyInfo.bic',
        value: 'INGBNL2A',
      },
      {
        labelMessage: 'messages.contact.companyInfo.iban',
        value: 'NL81INGB0653082878',
      },
    ],
  },
  {
    id: 'ebusiness',
    titleMessage: 'messages.contact.company.ebusiness.title',
    entity: 'FashionUnited Indicia BV',
    addressLines: ['Palmpolstraat 7', '1327 CA Almere'],
    phone: '+31 85 040 4866',
    phoneNoteMessage: 'messages.contact.company.ebusiness.phoneNote',
    codes: [
      {
        labelMessage: 'messages.contact.companyInfo.kvk',
        value: '17263340',
      },
      {
        labelMessage: 'messages.contact.companyInfo.vat',
        value: 'NL821301019B01',
      },
    ],
  },
  {
    id: 'tv',
    titleMessage: 'messages.contact.company.tv.title',
    addressLines: mediaAddress,
    email: mediaEmail,
    codes: [
      { labelMessage: 'messages.contact.companyInfo.kvk', value: mediaKvk },
      {
        labelMessage: 'messages.contact.companyInfo.vatBtw',
        value: mediaVatBtw,
      },
    ],
  },
  {
    id: 'careerCenters',
    titleMessage: 'messages.contact.company.careerCenters.title',
    addressLines: mediaAddress,
    phone: '+31 (0)20 237 43 10',
    email: 'jobs@fashionunited.com',
    codes: [
      { labelMessage: 'messages.contact.companyInfo.kvk', value: mediaKvk },
    ],
  },
];

const buildCompanyCards = (intl: IntlShape): ContactPageCompanyCard[] => (
  companyCardData.map((card) => ({
    id: card.id,
    title: intl.formatMessage(messages[card.titleMessage]),
    entity: card.entity,
    addressLines: card.addressLines,
    email: card.email,
    phone: card.phone,
    phoneNote: card.phoneNoteMessage
      ? intl.formatMessage(messages[card.phoneNoteMessage])
      : undefined,
    people: card.people?.map((person) => ({
      label: intl.formatMessage(messages[person.labelMessage]),
      name: intl.formatMessage(messages[person.nameMessage]),
    })),
    codes: card.codes.map((code) => ({
      label: intl.formatMessage(messages[code.labelMessage]),
      value: code.value,
    })),
  }))
);

const ContactPage = () => {
  const intl = useIntl();
  const { prefix } = useSiteContext();

  const breadcrumbs = [
    <HomeLink />,
    <LinkAndAnchor route="contact" passHref>
      {intl.formatMessage(messages.contact)}
    </LinkAndAnchor>,
  ];

  return (
    <ContactPageComponent
      breadcrumbs={breadcrumbs}
      title={intl.formatMessage(messages.contact)}
      lead={intl.formatMessage(messages['messages.contact.description'])}
      generalPhoneNumber="+31 20 615 4241"
      generalPhoneDescription={intl.formatMessage(
        messages['messages.contact.phoneDescription'],
      )}
      hoursLabel={intl.formatMessage(messages['messages.contact.hours'])}
      addressLabel={intl.formatMessage(messages['messages.contact.address'])}
      departmentsTitle={intl.formatMessage(
        messages['messages.contact.departments.title'],
      )}
      departmentsLead={intl.formatMessage(
        messages['messages.contact.departments.lead'],
      )}
      departments={buildDepartments(intl)}
      noteLabel={intl.formatMessage(messages['messages.contact.note'])}
      disclaimer={(
        // eslint-disable-next-line react/no-danger
        <div dangerouslySetInnerHTML={{
          __html: intl.formatMessage(messages['messages.contact.disclaimer']),
        }}
        />
      )}
      impressum={prefix === 'fashionunited.de' ? {
        title: intl.formatMessage(
          messages['messages.contact.impressum.title'],
        ),
      } : undefined}
      faqEyebrow={intl.formatMessage(
        messages['messages.contact.eyebrow.faq'],
      )}
      faqTitle={intl.formatMessage(messages['messages.contact.faq.title'])}
      faqLead={intl.formatMessage(messages['messages.contact.faq.lead'])}
      faq={(
        <SubscriptionFaq
          faqData={contactFaq(intl)}
          faqHeaderLabel=""
        />
      )}
      legalEyebrow={intl.formatMessage(
        messages['messages.contact.eyebrow.legal'],
      )}
      companyInfo={{
        title: intl.formatMessage(
          messages['messages.contact.companyInfo.title'],
        ),
        cards: buildCompanyCards(intl),
      }}
    />
  );
};

export default ContactPage;
